import {
  COOKIE_OID,
  getCookie,
  getUserIpFromCookieInWebCatalog,
} from '@utils/cookies';
import {safeLocalStorage} from '@utils/safeStorages';
import {
  SendCustomEventsBatchRequest,
  sendStatsBatch,
} from '@utils/sendStatsBatch';
import {getEnvConfig} from '@web-catalog/env';
import {useCallback, useEffect, useRef, useState} from 'react';
import {UAParser} from 'ua-parser-js';
import {v4} from 'uuid';

import {SSP_USER_ID} from '@web-catalog/components/Ads/SSP/consts';

import {AdFoxStatsEvent} from '@shared/Ads/Yandex/types';

import {useOmicronClientFlag} from '@web-catalog/hooks/omicron';

import {useAdfoxStore} from '@shared/Ads/Yandex/store';
import {useGetVkId} from '@web-catalog/store/auth';

const DEFAULT_INTERVAL_MS = 5000;

const convertEvents = (
  events: AdFoxStatsEvent[],
  userId: string,
  commonParams: Record<string, string>,
): SendCustomEventsBatchRequest => ({
  events: events.map(({eventName, payload}) => ({
    event_name: eventName,
    params: {...commonParams, ...payload},
    user_id: userId,
  })),
});

/**
 * периодически забирает накопившиеся ивенты и сливает их в ручку,
 *
 * также добавляет к событиям общие для всех ивентов параметры
 */

export const StatsSink = () => {
  const config = useOmicronClientFlag('ADFOX_CONFIG');
  const intervalMs = config.statSendingIntervalMs || DEFAULT_INTERVAL_MS;

  const consumeContextStats = useAdfoxStore().adFoxStatsEvents.get;

  const uidRef = useRef('');
  useEffect(() => {
    uidRef.current = safeLocalStorage.getItem(SSP_USER_ID) || v4();
  }, []);

  const sessionIdRef = useRef(v4());
  const getVkId = useGetVkId();
  const [userAgentData] = useState(() =>
    new UAParser(navigator.userAgent).getResult(),
  );

  const getCommonParams = useCallback(
    () => ({
      user_id: uidRef.current,
      session_id: sessionIdRef.current,
      vk_id: `${getVkId()}`,
      oid: getCookie(COOKIE_OID) || 'not_set',
      ip: getUserIpFromCookieInWebCatalog() || 'not_set',
      device_type: userAgentData.device?.type ?? 'desktop',
      browser_type:
        [userAgentData.browser?.name, userAgentData.browser?.version]
          .filter(Boolean)
          .join(' ') || 'unknown',
      device_os: userAgentData.os?.name || 'unknown',
    }),
    [getVkId, userAgentData],
  );

  useEffect(() => {
    const unloadHandler = () => {
      const stats = consumeContextStats();
      if (stats.length) {
        sendStatsBatch(
          convertEvents(stats, uidRef.current, getCommonParams()),
          getEnvConfig().STATS_URL,
          true,
        );
      }
    };

    window.addEventListener('beforeunload', unloadHandler);
    return () => {
      window.removeEventListener('beforeunload', unloadHandler);
    };
  }, [consumeContextStats, getCommonParams]);

  useEffect(() => {
    const interval = setInterval(() => {
      const stats = consumeContextStats();
      if (stats.length) {
        sendStatsBatch(
          convertEvents(stats, uidRef.current, getCommonParams()),
          getEnvConfig().STATS_URL,
        );
      }
    }, intervalMs);
    return () => {
      clearInterval(interval);
    };
  }, [consumeContextStats, getCommonParams, intervalMs]);

  return null;
};
